import { useEffect, useState, useMemo } from 'react'
import { format, subMonths, addMonths, startOfMonth } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  ChevronLeft, ChevronRight, Download, DollarSign, Clock,
  Info, Settings, Filter, FileSpreadsheet, Users, AlertTriangle
} from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'

// ── Grille salariale IDCC 1505 ─────────────────────────────────────
const CCN_GRID = [
  { level: 'E1',  label: 'Employé niveau 1',    hourlyRate: 12.063, monthly: 1829.54 },
  { level: 'E2',  label: 'Employé niveau 2',    hourlyRate: 12.134, monthly: 1840.34 },
  { level: 'E3',  label: 'Employé niveau 3',    hourlyRate: 12.147, monthly: 1842.34 },
  { level: 'E4',  label: 'Employé niveau 4',    hourlyRate: 12.400, monthly: 1880.75 },
  { level: 'E5',  label: 'Employé niveau 5',    hourlyRate: 12.486, monthly: 1893.71 },
  { level: 'E6',  label: 'Employé niveau 6',    hourlyRate: 12.785, monthly: 1939.06 },
  { level: 'E7',  label: 'Employé niveau 7',    hourlyRate: 12.882, monthly: 1953.86 },
  { level: 'AM1', label: 'Agent de maîtrise 1', hourlyRate: 16.023, monthly: 2430.18 },
  { level: 'AM2', label: 'Agent de maîtrise 2', hourlyRate: 16.296, monthly: 2471.68 },
  { level: 'C1',  label: 'Cadre 1',             hourlyRate: 19.929, monthly: 3022.65 },
  { level: 'C2',  label: 'Cadre 2',             hourlyRate: 22.077, monthly: 3348.42 },
]

interface EmployeeReport {
  id: string; firstName: string; lastName: string
  contractType: string; weeklyHours: number
  hourlyRate: number; conventionLevel: string
  totalHours: number; normalHours: number
  overtime25Hours: number; overtime50Hours: number
  night1Hours: number; night2Hours: number
  sundayMorningHours: number; sundayAfternoonHours: number
  holidayHours: number; may1stHours: number
  mealCount: number; mealVoucherEnabled: boolean
  paidAbsenceDays: number; unpaidAbsenceDays: number
  transport: number; primesTotal: number
  primesDetail: { label: string; amount: number }[]
  estimatedGross: number; employerCost: number
  absences: { type: string; code: string; color: string; isPaid: boolean; days: number }[]
}

interface PayrollData { startDate: string; endDate: string; employees: EmployeeReport[]; settings: any }

interface PayrollSettings {
  otThreshold1H: number; otThreshold2H: number
  nightStart1H: number; nightStart2H: number; nightEnd2H: number
  night1Rate: number; night2Rate: number
  sundayMajoRate: number; may1Rate: number
  rubriques: Record<string, string>
}

type ColKey = 'totalHours' | 'ot25' | 'ot50' | 'night1' | 'night2' | 'sundayMorning' | 'sundayAfternoon' | 'holiday' | 'may1' | 'meals' | 'absencePaid' | 'absenceUnpaid' | 'transport' | 'primes' | 'gross' | 'employerCost'

const ALL_COLS: { key: ColKey; label: string; short: string }[] = [
  { key: 'totalHours',      label: 'Heures totales',      short: 'H. tot.' },
  { key: 'ot25',            label: 'Supp +25%',           short: '+25%' },
  { key: 'ot50',            label: 'Supp +50%',           short: '+50%' },
  { key: 'night1',          label: 'Nuit 21-22h',         short: 'Nuit1' },
  { key: 'night2',          label: 'Nuit 22-6h',          short: 'Nuit2' },
  { key: 'sundayMorning',   label: 'Dim. matin',          short: 'Dim.↑' },
  { key: 'sundayAfternoon', label: 'Dim. après-midi',     short: 'Dim.↓' },
  { key: 'holiday',         label: 'Jours fériés',        short: 'Férié' },
  { key: 'may1',            label: '1er Mai',             short: '1 mai' },
  { key: 'meals',           label: 'Repas',               short: 'Repas' },
  { key: 'absencePaid',     label: 'Abs. payées',         short: 'Abs.P' },
  { key: 'absenceUnpaid',   label: 'Abs. non payées',     short: 'Abs.NP' },
  { key: 'transport',       label: 'Transport',           short: 'Transp.' },
  { key: 'primes',          label: 'Primes',              short: 'Primes' },
  { key: 'gross',           label: 'Brut estimé',         short: 'Brut' },
  { key: 'employerCost',    label: 'Coût employeur',      short: 'Coût emp.' },
]

const DEFAULT_VISIBLE: ColKey[] = ['totalHours','ot25','ot50','night1','night2','sundayAfternoon','may1','meals','absencePaid','transport','primes','gross']

const fmt2 = (n: number) => n.toFixed(2)
const fmtH = (n: number) => n > 0 ? `${n.toFixed(1)}h` : '—'
const fmtC = (n: number) => n > 0 ? `${n.toFixed(2)} €` : '—'

export default function Payroll() {
  const [periodMode, setPeriodMode] = useState<'month' | 'custom'>('month')
  const [monthDate, setMonthDate] = useState(() => startOfMonth(new Date()))
  const [fromDate, setFromDate] = useState(() => startOfMonth(new Date()).toISOString().split('T')[0])
  const [toDate, setToDate] = useState(() => {
    const d = startOfMonth(new Date()); d.setMonth(d.getMonth() + 1); d.setDate(d.getDate() - 1)
    return d.toISOString().split('T')[0]
  })
  const [data, setData] = useState<PayrollData | null>(null)
  const [loading, setLoading] = useState(false)
  const { current: establishment } = useEstablishment()
  const [showGrid, setShowGrid] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showColConfig, setShowColConfig] = useState(false)
  const [showEmpFilter, setShowEmpFilter] = useState(false)
  const [visibleCols, setVisibleCols] = useState<Set<ColKey>>(new Set(DEFAULT_VISIBLE))
  const [selectedEmps, setSelectedEmps] = useState<Set<string>>(new Set())
  const [settings, setSettings] = useState<PayrollSettings | null>(null)
  const [settingsForm, setSettingsForm] = useState<PayrollSettings | null>(null)
  const [savingSettings, setSavingSettings] = useState(false)
  const [anomalies, setAnomalies] = useState<{ unsigned: any[]; missingData: any[] }>({ unsigned: [], missingData: [] })
  const [showAnomalies, setShowAnomalies] = useState(true)

  const monthStr = format(monthDate, 'yyyy-MM')

  useEffect(() => {
    api.get('/payroll/settings').then(r => {
      setSettings(r.data)
      setSettingsForm(r.data)
    })
  }, [])

  useEffect(() => {
    if (!establishment) return
    setLoading(true)
    const params = periodMode === 'month'
      ? `month=${monthStr}&establishmentId=${establishment.id}`
      : `from=${fromDate}&to=${toDate}&establishmentId=${establishment.id}`
    api.get(`/payroll?${params}`)
      .then(r => {
        setData(r.data)
        setSelectedEmps(new Set(r.data.employees.map((e: EmployeeReport) => e.id)))
      })
      .finally(() => setLoading(false))

    // Load anomalies: unsigned/draft timesheets for this period
    const periodFrom = periodMode === 'month'
      ? `${monthStr}-01`
      : fromDate
    const periodTo = periodMode === 'month'
      ? (() => { const d = new Date(`${monthStr}-01`); d.setMonth(d.getMonth() + 1); d.setDate(d.getDate() - 1); return d.toISOString().split('T')[0] })()
      : toDate
    api.get(`/timesheets?from=${periodFrom}&to=${periodTo}&establishmentId=${establishment.id}`)
      .then(r => {
        const sheets: any[] = r.data
        const unsigned = sheets.filter(s => s.status === 'draft' || s.status === 'validated')
        const missingData = sheets.filter(s => s.status === 'signed' && (s.employee?.hourlyRate === 0 || !s.employee?.email))
        setAnomalies({ unsigned, missingData })
      }).catch(() => {})
  }, [monthStr, fromDate, toDate, periodMode, establishment])

  const filteredEmployees = useMemo(() =>
    data?.employees.filter(e => selectedEmps.size === 0 || selectedEmps.has(e.id)) ?? [],
    [data, selectedEmps]
  )

  async function saveSettings() {
    if (!settingsForm) return
    setSavingSettings(true)
    try {
      await api.put('/payroll/settings', settingsForm)
      setSettings(settingsForm)
      setShowSettings(false)
    } finally { setSavingSettings(false) }
  }

  function toggleCol(key: ColKey) {
    setVisibleCols(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s })
  }

  function toggleEmp(id: string) {
    setSelectedEmps(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })
  }

  function getCellValue(emp: EmployeeReport, col: ColKey): string {
    switch (col) {
      case 'totalHours':      return fmtH(emp.totalHours)
      case 'ot25':            return fmtH(emp.overtime25Hours)
      case 'ot50':            return fmtH(emp.overtime50Hours)
      case 'night1':          return fmtH(emp.night1Hours)
      case 'night2':          return fmtH(emp.night2Hours)
      case 'sundayMorning':   return fmtH(emp.sundayMorningHours)
      case 'sundayAfternoon': return fmtH(emp.sundayAfternoonHours)
      case 'holiday':         return fmtH(emp.holidayHours)
      case 'may1':            return fmtH(emp.may1stHours)
      case 'meals':           return emp.mealCount > 0 ? `${emp.mealCount}` : '—'
      case 'absencePaid':     return emp.paidAbsenceDays > 0 ? `${emp.paidAbsenceDays}j` : '—'
      case 'absenceUnpaid':   return emp.unpaidAbsenceDays > 0 ? `${emp.unpaidAbsenceDays}j` : '—'
      case 'transport':       return fmtC(emp.transport)
      case 'primes':          return fmtC(emp.primesTotal)
      case 'gross':           return `${emp.estimatedGross.toFixed(2)} €`
      case 'employerCost':    return `${emp.employerCost.toFixed(2)} €`
    }
  }

  function getCellClass(col: ColKey): string {
    const map: Partial<Record<ColKey, string>> = {
      ot25: 'text-amber-600 font-semibold',
      ot50: 'text-orange-600 font-semibold',
      night1: 'text-indigo-600 font-semibold',
      night2: 'text-indigo-800 font-semibold',
      sundayAfternoon: 'text-orange-500 font-semibold',
      may1: 'text-red-600 font-semibold',
      gross: 'text-teal-700 font-bold',
      employerCost: 'text-gray-700 font-bold',
    }
    return map[col] || 'text-gray-600'
  }

  function exportCSV() {
    if (!filteredEmployees.length) return
    const visibleList = ALL_COLS.filter(c => visibleCols.has(c.key))
    const headers = ['Employé', 'Niveau CCN', 'Taux €/h', ...visibleList.map(c => c.label)]
    const rows = filteredEmployees.map(emp => [
      `${emp.firstName} ${emp.lastName}`,
      emp.conventionLevel,
      fmt2(emp.hourlyRate),
      ...visibleList.map(c => getCellValue(emp, c.key).replace('—', '0').replace('h','').replace('€','').replace('j','').trim())
    ])
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(';')).join('\n')
    download('\uFEFF' + csv, `paie-${data?.startDate}-${data?.endDate}.csv`, 'text/csv;charset=utf-8;')
  }

  function exportExcel() {
    if (!filteredEmployees.length) return
    const visibleList = ALL_COLS.filter(c => visibleCols.has(c.key))
    // Build SYLK-format that Excel reads natively, or use basic XML spreadsheet
    const rows = [
      ['Employé', 'Niveau', 'Taux €/h', ...visibleList.map(c => c.label)],
      ...filteredEmployees.map(emp => [
        `${emp.firstName} ${emp.lastName}`, emp.conventionLevel, emp.hourlyRate,
        ...visibleList.map(c => {
          const v = getCellValue(emp, c.key)
          if (v === '—') return 0
          const num = parseFloat(v.replace(/[^0-9.-]/g, ''))
          return isNaN(num) ? v : num
        })
      ])
    ]
    // XML Spreadsheet (Excel 2003+ .xls compatible)
    const xmlRows = rows.map(row =>
      `  <Row>${row.map(cell =>
        typeof cell === 'number'
          ? `<Cell><Data ss:Type="Number">${cell}</Data></Cell>`
          : `<Cell><Data ss:Type="String">${String(cell).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</Data></Cell>`
      ).join('')}</Row>`
    ).join('\n')
    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<?mso-application progid="Excel.Sheet"?>\n<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n<Worksheet ss:Name="Rapport paie">\n<Table>\n${xmlRows}\n</Table>\n</Worksheet>\n</Workbook>`
    download(xml, `paie-${data?.startDate}-${data?.endDate}.xls`, 'application/vnd.ms-excel')
  }

  function download(content: string, filename: string, mime: string) {
    const blob = new Blob([content], { type: mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click()
    URL.revokeObjectURL(url)
  }

  const totalGross = filteredEmployees.reduce((s, e) => s + e.estimatedGross, 0)
  const totalEmployerCost = filteredEmployees.reduce((s, e) => s + e.employerCost, 0)
  const totalOT = filteredEmployees.reduce((s, e) => s + e.overtime25Hours + e.overtime50Hours, 0)
  const totalNight = filteredEmployees.reduce((s, e) => s + e.night1Hours + e.night2Hours, 0)
  const totalMeals = filteredEmployees.reduce((s, e) => s + e.mealCount, 0)

  const visibleList = ALL_COLS.filter(c => visibleCols.has(c.key))

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <DollarSign size={22} className="text-green-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Rapport de paie</h1>
            <p className="text-xs text-gray-400">Convention IDCC {settings ? '1505' : '…'} — Calcul automatique des variables</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowGrid(!showGrid)}
            className="flex items-center gap-1.5 border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 text-sm px-3 py-2 rounded-lg">
            <Info size={14} /> Grille CCN
          </button>
          <button onClick={() => setShowSettings(true)}
            className="flex items-center gap-1.5 border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 text-sm px-3 py-2 rounded-lg">
            <Settings size={14} /> Paramétrage
          </button>
          <button onClick={() => setShowColConfig(!showColConfig)}
            className="flex items-center gap-1.5 border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 text-sm px-3 py-2 rounded-lg">
            <Filter size={14} /> Colonnes
          </button>
          <button onClick={() => setShowEmpFilter(!showEmpFilter)}
            className={`flex items-center gap-1.5 border text-sm px-3 py-2 rounded-lg ${showEmpFilter ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}>
            <Users size={14} /> Salariés
          </button>
          <button onClick={exportCSV} disabled={!filteredEmployees.length}
            className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm px-3 py-2 rounded-lg">
            <Download size={14} /> CSV
          </button>
          <button onClick={exportExcel} disabled={!filteredEmployees.length}
            className="flex items-center gap-1.5 bg-green-700 hover:bg-green-800 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm px-3 py-2 rounded-lg">
            <FileSpreadsheet size={14} /> Excel
          </button>
        </div>
      </div>

      {/* Grille CCN panel */}
      {showGrid && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
          <h2 className="font-semibold text-blue-900 mb-3 text-sm">Grille salariale IDCC 1505 — au 1er juin 2025 (+1,70%)</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {CCN_GRID.map(row => (
              <div key={row.level} className="bg-white rounded-lg p-2.5 text-xs border border-blue-100">
                <span className="font-bold text-blue-700 text-sm">{row.level}</span>
                <span className="text-gray-500 ml-1">{row.label}</span>
                <div className="mt-1 font-semibold text-gray-900">{row.hourlyRate.toFixed(3)} €/h</div>
                <div className="text-gray-400">{row.monthly.toFixed(2)} €/mois</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-blue-700 space-y-0.5">
            <p className="font-semibold mb-1">Règles de majoration IDCC 1505 :</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5">
              <p>• Heures supp. 36h→43h : <strong>+25%</strong></p>
              <p>• Heures supp. ≥44h : <strong>+50%</strong></p>
              <p>• Nuit 21h→22h : <strong>+5%</strong></p>
              <p>• Nuit 22h→6h : <strong>+20%</strong></p>
              <p>• Dim. matin ≤400m² : <strong>pas de majoration légale</strong></p>
              <p>• Dim. après-midi (arrêté municipal) : <strong>+100%</strong></p>
              <p>• 1er mai travaillé : <strong>+100%</strong></p>
              <p>• Contingent HS annuel : <strong>180h max</strong></p>
            </div>
          </div>
        </div>
      )}

      {/* Column configurator */}
      {showColConfig && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Colonnes affichées</h3>
          <div className="flex flex-wrap gap-2">
            {ALL_COLS.map(col => (
              <button key={col.key} onClick={() => toggleCol(col.key)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium border transition-colors ${visibleCols.has(col.key) ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-500 hover:border-blue-300'}`}>
                {col.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Employee filter */}
      {showEmpFilter && data && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Filtrer les salariés</h3>
            <div className="flex gap-2 text-xs">
              <button onClick={() => setSelectedEmps(new Set(data.employees.map(e => e.id)))} className="text-blue-600 hover:underline">Tous</button>
              <button onClick={() => setSelectedEmps(new Set())} className="text-gray-500 hover:underline">Aucun</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.employees.map(emp => (
              <button key={emp.id} onClick={() => toggleEmp(emp.id)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium border transition-colors ${selectedEmps.has(emp.id) ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-gray-300 text-gray-500 hover:border-indigo-300'}`}>
                {emp.firstName} {emp.lastName}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Period selector */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button onClick={() => setPeriodMode('month')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${periodMode === 'month' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
            Mois
          </button>
          <button onClick={() => setPeriodMode('custom')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${periodMode === 'custom' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
            Période libre
          </button>
        </div>
        {periodMode === 'month' ? (
          <div className="flex items-center gap-1 bg-white border rounded-lg shadow-sm">
            <button onClick={() => setMonthDate(d => startOfMonth(subMonths(d, 1)))} className="p-2 hover:bg-gray-50 rounded-l-lg"><ChevronLeft size={18} /></button>
            <span className="px-5 text-sm font-medium text-gray-700 capitalize whitespace-nowrap">
              {format(monthDate, 'MMMM yyyy', { locale: fr })}
            </span>
            <button onClick={() => setMonthDate(d => startOfMonth(addMonths(d, 1)))} className="p-2 hover:bg-gray-50 rounded-r-lg"><ChevronRight size={18} /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span className="text-gray-400">→</span>
            <input type="date" value={toDate} min={fromDate} onChange={e => setToDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        )}
        {periodMode === 'month' && (
          <button onClick={() => setMonthDate(startOfMonth(new Date()))} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Ce mois</button>
        )}
      </div>

      {/* Anomalies panel */}
      {(anomalies.unsigned.length > 0 || anomalies.missingData.length > 0) && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setShowAnomalies(v => !v)}
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-amber-100/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-500" />
              <span className="font-semibold text-amber-800 text-sm">
                Anomalies à corriger avant export
              </span>
              <span className="bg-amber-200 text-amber-800 text-xs font-bold px-2 py-0.5 rounded-full">
                {anomalies.unsigned.length + anomalies.missingData.length}
              </span>
            </div>
            <span className="text-amber-600 text-sm">{showAnomalies ? '▲' : '▼'}</span>
          </button>
          {showAnomalies && (
            <div className="px-5 pb-4 space-y-3">
              {anomalies.unsigned.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                    Feuilles non signées ({anomalies.unsigned.length})
                  </p>
                  <div className="space-y-1">
                    {anomalies.unsigned.map((ts: any) => (
                      <div key={ts.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-100">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ts.status === 'draft' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-700'}`}>
                            {ts.status === 'draft' ? 'Brouillon' : 'À signer'}
                          </span>
                          <span className="text-sm font-medium text-gray-800">
                            {ts.employee?.firstName} {ts.employee?.lastName !== '-' ? ts.employee?.lastName : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500">{ts.totalHours?.toFixed(1)}h</span>
                          <a href="/timesheets" className="text-xs text-blue-600 hover:underline">Corriger →</a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {anomalies.missingData.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                    Données manquantes ({anomalies.missingData.length})
                  </p>
                  <div className="space-y-1">
                    {anomalies.missingData.map((ts: any) => (
                      <div key={ts.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-100">
                        <span className="text-sm font-medium text-gray-800">
                          {ts.employee?.firstName} {ts.employee?.lastName !== '-' ? ts.employee?.lastName : ''}
                        </span>
                        <span className="text-xs text-red-500">Email / taux manquant</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-amber-600 pt-1">
                Validez et faites signer toutes les feuilles avant de générer l'export final.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Summary cards */}
      {filteredEmployees.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Masse brute', value: `${totalGross.toFixed(0)} €`, color: 'bg-green-100', icon: DollarSign, iconColor: 'text-green-600' },
            { label: 'Coût employeur', value: `${totalEmployerCost.toFixed(0)} €`, color: 'bg-teal-100', icon: DollarSign, iconColor: 'text-teal-600' },
            { label: 'Heures supp.', value: `${totalOT.toFixed(1)}h`, color: 'bg-amber-100', icon: Clock, iconColor: 'text-amber-600' },
            { label: 'Heures de nuit', value: `${totalNight.toFixed(1)}h`, color: 'bg-indigo-100', icon: Clock, iconColor: 'text-indigo-600' },
            { label: 'Repas', value: `${totalMeals}`, color: 'bg-orange-100', icon: DollarSign, iconColor: 'text-orange-600' },
          ].map(({ label, value, color, icon: Icon, iconColor }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
              <div className={`${color} p-2.5 rounded-lg`}><Icon size={16} className={iconColor} /></div>
              <div>
                <p className="text-lg font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-gray-400 text-sm">Chargement...</div>
      ) : filteredEmployees.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-gray-400 text-sm">
          Aucune donnée. Générez et validez des feuilles d'heures d'abord.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap sticky left-0 bg-gray-50">Employé</th>
                  <th className="px-3 py-3 text-center font-semibold text-gray-500 text-xs whitespace-nowrap">Niveau</th>
                  <th className="px-3 py-3 text-center font-semibold text-gray-500 text-xs whitespace-nowrap">€/h</th>
                  {visibleList.map(col => (
                    <th key={col.key} className="px-3 py-3 text-center font-semibold text-gray-500 text-xs whitespace-nowrap">{col.short}</th>
                  ))}
                  {settings && (
                    <th className="px-3 py-3 text-center font-semibold text-gray-500 text-xs whitespace-nowrap">Rubriques</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredEmployees.map(emp => (
                  <tr key={emp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 sticky left-0 bg-white hover:bg-gray-50">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {emp.firstName[0]}{emp.lastName[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 whitespace-nowrap">{emp.firstName} {emp.lastName}</p>
                          <p className="text-xs text-gray-400">{emp.contractType} · {emp.weeklyHours}h/sem</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{emp.conventionLevel}</span>
                    </td>
                    <td className="px-3 py-3 text-center text-gray-600 font-mono text-xs">{fmt2(emp.hourlyRate)}</td>
                    {visibleList.map(col => (
                      <td key={col.key} className={`px-3 py-3 text-center ${getCellClass(col.key)}`}>
                        {getCellValue(emp, col.key)}
                      </td>
                    ))}
                    {settings && (
                      <td className="px-3 py-3 text-center">
                        <button className="text-xs text-gray-400 hover:text-blue-600" title="Voir rubriques">
                          {Object.values(settings.rubriques || {}).slice(0,3).join(', ')}…
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={3} className="px-4 py-3 font-semibold text-gray-700">Total ({filteredEmployees.length} salarié{filteredEmployees.length > 1 ? 's' : ''})</td>
                  {visibleList.map(col => {
                    const numericCols: ColKey[] = ['totalHours','ot25','ot50','night1','night2','sundayMorning','sundayAfternoon','holiday','may1','meals','absencePaid','absenceUnpaid','transport','primes','gross','employerCost']
                    if (!numericCols.includes(col.key)) return <td key={col.key} />
                    const sum = filteredEmployees.reduce((s, emp) => {
                      const v = parseFloat(getCellValue(emp, col.key).replace(/[^0-9.-]/g, ''))
                      return s + (isNaN(v) ? 0 : v)
                    }, 0)
                    return (
                      <td key={col.key} className={`px-3 py-3 text-center font-bold ${getCellClass(col.key)}`}>
                        {col.key === 'meals' ? sum : col.key.includes('Abs') || col.key === 'meals' ? sum : `${sum.toFixed(col.key === 'gross' || col.key === 'employerCost' || col.key === 'transport' || col.key === 'primes' ? 2 : 1)}${col.key === 'gross' || col.key === 'employerCost' || col.key === 'transport' || col.key === 'primes' ? ' €' : 'h'}`}
                      </td>
                    )
                  })}
                  {settings && <td />}
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="px-4 py-3 bg-amber-50 border-t border-amber-100">
            <p className="text-xs text-amber-700">
              <strong>⚠️ Brut estimé :</strong> calcul indicatif basé sur les shifts planifiés + règles configurées.
              Ne tient pas compte des primes d'ancienneté, avantages en nature ni des cotisations exactes.
              À titre informatif uniquement — confirmez avec votre expert-comptable.
            </p>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {showSettings && settingsForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowSettings(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
              <h2 className="font-bold text-gray-900">Paramétrage paie</h2>
              <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-6 space-y-6">
              {/* Convention rules */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Règles de majoration convention</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'otThreshold1H',  label: 'Seuil HS +25% (h/sem)',  type: 'number' },
                    { key: 'otThreshold2H',  label: 'Seuil HS +50% (h/sem)',  type: 'number' },
                    { key: 'nightStart1H',   label: 'Nuit tranche 1 — début', type: 'number' },
                    { key: 'nightStart2H',   label: 'Nuit tranche 2 — début', type: 'number' },
                    { key: 'nightEnd2H',     label: 'Nuit tranche 2 — fin',   type: 'number' },
                    { key: 'night1Rate',     label: 'Taux nuit T1 (ex: 0.05)', type: 'number' },
                    { key: 'night2Rate',     label: 'Taux nuit T2 (ex: 0.20)', type: 'number' },
                    { key: 'sundayMajoRate', label: 'Taux dim. après-midi',   type: 'number' },
                    { key: 'may1Rate',       label: 'Taux 1er mai',           type: 'number' },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="block text-xs text-gray-600 mb-1">{label}</label>
                      <input
                        type="number" step="0.01"
                        value={(settingsForm as any)[key]}
                        onChange={e => setSettingsForm(f => f ? { ...f, [key]: parseFloat(e.target.value) } : f)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Rubriques mapping */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Mapping rubriques logiciel de paie</h3>
                <p className="text-xs text-gray-400 mb-3">Codes rubriques utilisés pour l'export vers Silae, Sage, etc.</p>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(settingsForm.rubriques).map(([key, val]) => {
                    const labels: Record<string, string> = {
                      normalHours: 'Heures normales', ot25: 'H. supp +25%', ot50: 'H. supp +50%',
                      night1: 'Nuit T1 (+5%)', night2: 'Nuit T2 (+20%)', sunday: 'Dimanche',
                      holidays: 'Jours fériés', meals: 'Tickets repas', transport: 'Transport',
                      primes: 'Primes', absencePaid: 'Absence payée', absenceUnpaid: 'Absence non payée'
                    }
                    return (
                      <div key={key}>
                        <label className="block text-xs text-gray-600 mb-1">{labels[key] || key}</label>
                        <input
                          value={val}
                          onChange={e => setSettingsForm(f => f ? { ...f, rubriques: { ...f.rubriques, [key]: e.target.value } } : f)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={`Code rubrique (ex: ${key === 'normalHours' ? '100' : '...'})`}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 sticky bottom-0">
              <button onClick={() => setShowSettings(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={saveSettings} disabled={savingSettings}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg">
                {savingSettings ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
